import { coresNomes, light_mode } from '@constants/Color'

// Teto de aninhamento de PASTAS (1-based: 1 = pasta raiz). Espelha
// `MAX_FOLDER_DEPTH` do backend (`core/folder_hierarchy.py`), que é quem valida.
//
// ★ FONTE ÚNICA — não redeclare este número em nenhum componente. O módulo já
// pagou essa conta com `TASK_MAX_LEVEL`, que existia em três declarações
// independentes e permitia modal e listagem discordarem sobre onde a árvore
// termina. Consomem este teto a sidebar (item "Adicionar subpasta") e o modal de
// mover pasta (destinos que estourariam o limite).
export const FOLDER_MAX_LEVEL = 3

// DEPRECATED (customizar-status-tarefas): statuses are now per-folder, custom,
// and resolved from the Vuex `statusesByFolder` slice (name/color/category by
// `statusId`). These legacy enums/labels remain ONLY as a transitional
// fallback for code paths that still carry a `task.status` literal and for the
// 1:1 category mapping (todo→open, doing→in_progress, done→done, closed→closed).
// Do NOT add new consumers — render from the folder statuses slice instead.
export const TASK_STATUS = {
  TODO: 'todo',
  DOING: 'doing',
  DONE: 'done',
  CLOSED: 'closed',
}

// DEPRECATED — see TASK_STATUS above. Transitional labels for the legacy
// literal; custom statuses carry their own `name`.
export const TASK_STATUS_LABELS = {
  todo: 'A Fazer',
  doing: 'Em Andamento',
  done: 'Finalizada',
  overdue: 'Atrasada',
  closed: 'Fechado',
}

// Mensagens de feedback de rodapé (R1 — effects-task) para os fluxos de alto
// volume de create/move/reorder na List e no Kanban. O rodapé (`$showSnack`) é
// o canal padrão AAWZ para ações repetitivas — substitui o `$showSnackModal` de
// tela cheia nesses fluxos, alinhando com a convenção global "Snack for API
// Feedback". Pt-BR com acento UTF-8 real (nunca `\uXXXX`).
export const TASK_SNACK_MESSAGES = {
  TASK_CREATED: 'Tarefa criada.',
  TASK_MOVED: 'Tarefa movida.',
  ORDER_UPDATED: 'Ordem atualizada.',
}

// Category → display label + design-system color name. Drives the
// category selector in the status form and any category-badge rendering.
// The 4 categories are fixed (the status set is customizable, the category
// taxonomy is not).
export const TASK_STATUS_CATEGORIES = [
  { value: 'open', label: 'A Fazer', color: 'secondary' },
  { value: 'in_progress', label: 'Em Andamento', color: 'info' },
  { value: 'done', label: 'Concluído', color: 'success' },
  { value: 'closed', label: 'Fechado', color: coresNomes.roxo },
]

export const TASK_STATUS_CATEGORY_LABELS = {
  open: 'A Fazer',
  in_progress: 'Em Andamento',
  done: 'Concluído',
  closed: 'Fechado',
}

// Category → design-system color, DERIVED from TASK_STATUS_CATEGORIES so the
// mapping lives in a single place. Consumed by `task-status-chip` (fallback
// color when a status has no explicit color) and `folder-status-table`
// (category badge color) — neither should re-declare the map locally.
export const TASK_STATUS_CATEGORY_COLORS = TASK_STATUS_CATEGORIES.reduce(
  function (map, category) {
    map[category.value] = category.color
    return map
  },
  {}
)

// Fixed category taxonomy (the status SET is customizable per folder; the 4
// categories are not). Single source consumed by the chip, store getters and
// isOverdue — do NOT re-declare these maps locally.
export const TASK_STATUS_CATEGORY = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  DONE: 'done',
  CLOSED: 'closed',
}

// Terminal categories: a task in one of these is "final" — never shown as
// overdue and counted as concluded. Mirrors the backend done/closed semantics.
export const FINAL_STATUS_CATEGORIES = {
  [TASK_STATUS_CATEGORY.DONE]: true,
  [TASK_STATUS_CATEGORY.CLOSED]: true,
}

// Legacy `task.status` literal → category, for transitional rows that still
// carry the old string instead of a per-folder statusId (pre-migration / tests).
export const LEGACY_STATUS_TO_CATEGORY = {
  [TASK_STATUS.TODO]: TASK_STATUS_CATEGORY.OPEN,
  [TASK_STATUS.DOING]: TASK_STATUS_CATEGORY.IN_PROGRESS,
  [TASK_STATUS.DONE]: TASK_STATUS_CATEGORY.DONE,
  [TASK_STATUS.CLOSED]: TASK_STATUS_CATEGORY.CLOSED,
}

// Category → hex color. Single source for the List-view group dot in the
// quick-view (cross-folder) mode: cross-folder tasks carry heterogeneous
// custom-status colors, so the group dot uses the CATEGORY color instead.
// Derived from the DS palette (`secondary`/`success` from `light_mode`, `roxo`
// from `coresNomes`; `in_progress` uses the Vuetify theme `info` token). Kept
// as a NAMED map here (not loose hex in the getter) so the getter derives the
// rgb triplet via `hexToRgbString`.
export const TASK_STATUS_CATEGORY_HEX = {
  [TASK_STATUS_CATEGORY.OPEN]: light_mode.secondary,
  [TASK_STATUS_CATEGORY.IN_PROGRESS]: '#2196F3',
  [TASK_STATUS_CATEGORY.DONE]: light_mode.success,
  [TASK_STATUS_CATEGORY.CLOSED]: coresNomes.roxo,
}

// Profundidade máxima da hierarquia de tarefas, em nível absoluto (1-based):
// 1 = tarefa raiz; TASK_MAX_LEVEL = último nível que ainda aceita filhos.
// Fonte única do módulo — não redeclarar em componente.
export const TASK_MAX_LEVEL = 6

// A mesma regra na escala `depth` usada pelas linhas inline da listagem, onde
// depth = level - 1 (uma linha em depth D é uma tarefa de nível D+1).
// Derivada de propósito: subir TASK_MAX_LEVEL sem lembrar de ajustar a escala
// depth deixaria modal e listagem discordando sobre onde a árvore termina —
// é essa classe de bug que a derivação elimina.
export const TASK_MAX_INLINE_DEPTH = TASK_MAX_LEVEL - 1

// Política de volume das ações em lote: acima deste número de tarefas
// selecionadas, alterar e excluir pedem confirmação antes de disparar (cada uma
// é uma requisição por tarefa). Mover NÃO consome o limiar — é uma única
// requisição em bloco. Fonte única do módulo: nenhum componente repete o valor.
export const TASK_BATCH_VOLUME_WARNING_THRESHOLD = 50

// Política de volume do RENDER: acima deste número de linhas-raiz, "Carregar
// todas" pede confirmação antes de buscar o resto do servidor e abrir todas as
// janelas. É outro eixo do limiar de lote acima — aquele conta REQUISIÇÕES, este
// conta LINHAS NO DOM — por isso um valor próprio em vez de reaproveitar os 50.
//
// De onde vem o 300: cada linha renderizada custa ~1,17 MB de heap (medido em
// 2026-08-12; ~0,90 MB depois do `task-lazy-menu`), então 1 GB é cruzado por
// volta de 511 linhas. 300 deixa margem antes disso e ainda cobre o caso comum
// de "quero ver a lista inteira" numa faixa pequena sem fricção nenhuma.
export const TASK_RENDER_VOLUME_WARNING_THRESHOLD = 300

// Espera antes de repetir um save de descrição que foi recusado porque o save
// anterior do MESMO campo ainda estava em voo (`saveField` retorna cedo e em
// silêncio nesse caso). NÃO é auto-save por digitação: o único gatilho de save da
// descrição é o clique fora do editor.
//
// Por que não há auto-save por digitação: cada PUT de `description` grava uma
// linha no painel de Atividade da tarefa, com valor antes → depois. Salvar a cada
// pausa de digitação encheria o histórico de versões intermediárias e o tornaria
// inauditável. Uma sessão de edição custa UM evento.
export const DESCRIPTION_SAVE_RETRY_MS = 400

// Tempo que o rótulo "Salvo" fica visível no cabeçalho do card antes de sumir.
// Não há mínimo forçado no "Salvando…": ao contrário das ações disparadas por
// clique de botão, ninguém está olhando este indicador esperando confirmação —
// ele é reasseguramento periférico, e um mínimo aqui só atrasaria a volta ao
// estado de leitura.
export const DESCRIPTION_SAVED_FLAG_MS = 2000

export const TASK_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent',
}

export const TASK_PRIORITY_LABELS = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
}

// Rótulo do estado "sem prioridade" (priority === null). Fica FORA de
// TASK_PRIORITY_LABELS de propósito: null não é um valor do domínio, é a
// ausência dele. Incluí-lo no mapa levaria alguém a espelhá-lo em
// TASK_PRIORITY_RANK, e aí o comparador passaria a tratar "não decidido" como
// um degrau da escala em vez de mandá-lo para o fim.
export const TASK_PRIORITY_NONE_LABEL = 'Sem prioridade'
// Cor do mesmo estado — o fallback que `task-card.vue` usa. Fora do mapa pela
// mesma razão do rótulo. ⚠️ `--secondary` resolve para lilás (`crm/CLAUDE.md`
// §9.1 D1); a troca do token é follow-up de DS e muda aqui, uma vez.
export const TASK_PRIORITY_NONE_COLOR = 'var(--secondary)'

export const TASK_PRIORITY_COLORS = {
  low: 'var(--secondary)',
  medium: 'var(--info)',
  high: coresNomes.laranjaDinamico,
  urgent: 'var(--danger)',
}

// Severity rank for priority sorting. Higher number = more urgent. The
// comparator orders ascending by default (urgent first); the direction
// toggle reverses it. Null/unknown priorities sort last (handled by the
// comparator's null-last guard, never by this map).
export const TASK_PRIORITY_RANK = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1,
}

// Sort field definitions surfaced by the toolbar Sort control. `field: null`
// is the default "Manual (arrastar)" option that falls back to the existing
// sortOrder (null-last) behavior. `type` drives the typed comparator in
// store/getters.js. Labels are pt-BR with real UTF-8 characters.
export const TASK_SORT_FIELDS = [
  { field: null, label: 'Manual (arrastar)', type: 'manual' },
  { field: 'dueDate', label: 'Data de vencimento', type: 'date' },
  { field: 'priority', label: 'Prioridade', type: 'priority' },
  { field: 'title', label: 'Título', type: 'string' },
  { field: 'startDate', label: 'Data inicial', type: 'date' },
  { field: 'createdAt', label: 'Data de criação', type: 'date' },
  { field: 'responsavel', label: 'Responsável', type: 'string' },
  { field: 'cliente', label: 'Cliente', type: 'string' },
  { field: 'status', label: 'Status', type: 'status' },
]

// Registry (single source of truth) das colunas configuráveis da List view.
// Cada descriptor: `key` (namespace persistido na preferência — DEVE casar com
// a whitelist do backend), `label` (pt-BR, UTF-8 real), `field` (sort-chain;
// null = não-sortável), `fixed` (Nome/Ações: sempre visíveis, nunca
// armazenadas na preferência), `defaultVisible` (visível quando não há
// preferência salva — reproduz as colunas atuais), `cols`/`colClass` (grid
// Vuetify, espelhando o `task-item.vue`).
//
// Built-ins novos: `startDate` e `tags` (defaultVisible=false — só aparecem
// quando o usuário ativa). `status` tem key própria (hoje renderiza junto de
// `priority` na mesma célula; vira coluna independente no grid dinâmico).
// Custom fields NÃO entram aqui — geram descriptors dinâmicos `cf:<uuid>` no
// getter `tarefas/visibleListColumns`.
//
// Os descriptors são CONGELADOS (Object.freeze): componentes devem tratá-los
// como read-only — qualquer customização vive na preferência (Vuex), nunca em
// mutação do registry.
// `resizable`/`minPx`/`maxPx` (v3 — task-list-column-sizing PX): habilitam o
// resize por arraste 1:1 no header. `minPx`/`maxPx` são os limites de largura
// (PIXELS) por coluna; ficam DENTRO do range global [40,1200] do backend.
// Colunas de texto/pessoa: 80..600. Colunas compactas (datas/prioridade/
// status/tags): 64..360. `actions` 16..64 com `defaultPx: 16` (cabe só o menu
// de 1 ícone — coluna enxuta pra não inflar a largura total da régua nem
// estourar 100%; por não ser redimensionável, escapa do piso global). `cols`/
// `colClass` permanecem como seed default (col→px) e breakpoint de exibição.
const _COLUMN_DEFINITIONS = [
  {
    key: 'title',
    label: 'Nome',
    field: 'title',
    fixed: true,
    defaultVisible: true,
    cols: '3',
    colClass: 'pr-3',
    resizable: true,
    // Piso 300px (exceção às colunas de texto 80..600). `title` é o grower
    // (ocupa a sobra, 1º a encolher) e o grid é customizável por campos, então
    // um piso alto garante que o nome nunca fique pequeno demais nem menor que
    // as demais colunas. 300px = 25% do container de referência (1200px).
    minPx: 300,
    maxPx: 600,
  },
  {
    key: 'responsavel',
    label: 'Responsável',
    field: 'responsavel',
    fixed: false,
    defaultVisible: true,
    cols: '2',
    colClass: 'pr-3',
    resizable: true,
    // Largura default enxuta: a célula mostra avatar(es) + nome curto — os
    // 200px herdados de `cols: '2'` deixavam a régua espaçada demais. Quem já
    // redimensionou a coluna mantém a largura salva (isto é só o default).
    defaultPx: 150,
    minPx: 80,
    maxPx: 600,
  },
  {
    key: 'participantes',
    label: 'Participantes',
    field: null,
    fixed: false,
    defaultVisible: true,
    cols: '2',
    colClass: 'pr-3',
    resizable: true,
    defaultPx: 150,
    minPx: 80,
    maxPx: 600,
  },
  {
    key: 'cliente',
    label: 'Cliente',
    field: 'cliente',
    fixed: false,
    defaultVisible: true,
    cols: '2',
    colClass: 'pr-3 d-none d-lg-flex',
    resizable: true,
    defaultPx: 150,
    minPx: 80,
    maxPx: 600,
  },
  {
    key: 'dueDate',
    label: 'Prazo',
    field: 'dueDate',
    fixed: false,
    defaultVisible: true,
    cols: 'auto',
    colClass: 'pr-3',
    resizable: true,
    // Data formatada (dd/mm/aaaa) cabe em 110px — o fallback de 120px sobrava.
    defaultPx: 110,
    minPx: 64,
    maxPx: 360,
  },
  {
    key: 'startDate',
    label: 'Data inicial',
    field: 'startDate',
    fixed: false,
    defaultVisible: false,
    cols: 'auto',
    colClass: 'pr-3',
    resizable: true,
    defaultPx: 110,
    minPx: 64,
    maxPx: 360,
  },
  {
    key: 'priority',
    label: 'Prioridade',
    field: 'priority',
    fixed: false,
    defaultVisible: true,
    cols: 'auto',
    colClass: 'pr-2 d-none d-sm-flex',
    resizable: true,
    // Piso REAL de 50px (`shrinkFloor`): a célula continua encolhendo com a
    // régua, mas nunca abaixo de 50px — largura em que a bandeirinha de
    // prioridade ainda aparece inteira. Sem o piso, o `minWidth: 0` do
    // shrink-to-fit (v4) comprime a coluna até o ícone sumir quando a régua
    // fica apertada (o `status` ao lado é `noShrink` e não cede espaço).
    shrinkFloor: true,
    // Só a bandeirinha de prioridade: 60px em vez dos 120px do fallback.
    defaultPx: 60,
    minPx: 50,
    maxPx: 360,
  },
  {
    key: 'status',
    label: 'Status',
    field: 'status',
    fixed: false,
    defaultVisible: true,
    cols: 'auto',
    colClass: 'pr-2 d-none d-sm-flex justify-end',
    resizable: true,
    // Piso de 60px (e `noShrink`): a célula renderiza o status na versão
    // RESUMIDA (só a bolinha na cor do status — `compact` no TaskStatusChip),
    // então 60px já acomodam o ponto + o padding do gatilho sem encostar na
    // coluna de prioridade à esquerda. `noShrink` impede que o shrink-to-fit
    // (v4) desfaça o piso quando falta largura na régua.
    noShrink: true,
    // Versão resumida (só a bolinha): nasce já no piso de 60px.
    defaultPx: 60,
    minPx: 60,
    maxPx: 360,
  },
  {
    key: 'tags',
    label: 'Tags',
    field: null,
    fixed: false,
    defaultVisible: false,
    cols: 'auto',
    colClass: 'pr-2',
    resizable: true,
    minPx: 64,
    maxPx: 360,
  },
  {
    key: 'actions',
    label: '',
    field: null,
    fixed: true,
    defaultVisible: true,
    cols: 'auto',
    colClass: 'd-none d-sm-flex justify-end',
    // Não-redimensionável: cabe só o menu de 1 ícone, e a faixa direita desta
    // coluna é sobreposta pelo botão `+` (config de colunas) no header — um
    // handle de resize ali conflitaria com o `+`.
    resizable: false,
    // 16px: no header esta célula é VAZIA (só reserva a faixa do `+`), e nas
    // linhas ela ancora o menu `…` (v-btn x-small, 20px) alinhado à direita.
    // Sem `defaultPx` ela caía no fallback de 120px clampado no `maxPx` → 64px
    // de espaço morto na régua. Como a coluna não é redimensionável, o
    // `defaultPx` nunca é persistido e por isso pode ficar abaixo do piso
    // global de 40px do backend (ver `defaultPxFor`).
    defaultPx: 16,
    minPx: 16,
    maxPx: 64,
  },
]

export const TASK_LIST_COLUMN_DEFINITIONS = Object.freeze(
  _COLUMN_DEFINITIONS.map(function (def) {
    return Object.freeze(def)
  })
)

// Colunas visíveis quando NÃO há preferência salva (defaultVisible, em ordem
// do registry, incluindo as fixas title/actions). É o fallback defensivo dos
// componentes de grid quando o getter `tarefas/visibleListColumns` não está
// disponível — e reproduz EXATAMENTE as colunas pré-refactor (com a única
// divergência intencional documentada: `priority` e `status` agora são
// células independentes em vez de combinadas numa única célula `auto`).
export const TASK_LIST_DEFAULT_COLUMNS = Object.freeze(
  TASK_LIST_COLUMN_DEFINITIONS.filter(function (def) {
    return def.defaultVisible
  })
)

// Pt-BR labels for task field names surfaced in the history timeline.
// Backend (history_writer.py:write_task_field_changes) writes raw snake_case
// names like 'start_date'; the timeline used to print them verbatim. Map both
// snake_case and camelCase shapes so we render "Data inicial" regardless of
// which payload variant the backend ships.
export const TASK_FIELD_LABELS = {
  title: 'Título',
  description: 'Descrição',
  status: 'Status',
  id_status: 'Status',
  idStatus: 'Status',
  priority: 'Prioridade',
  start_date: 'Data inicial',
  startDate: 'Data inicial',
  due_date: 'Data de vencimento',
  dueDate: 'Data de vencimento',
  id_sprint: 'Lista',
  idSprint: 'Lista',
  id_folder: 'Pasta',
  idFolder: 'Pasta',
  parent_id: 'Tarefa pai',
  parentId: 'Tarefa pai',
}

/**
 * Builds the Tarefas top-level nav-bar routes.
 *
 * Sempre inclui "Visão Geral" + "Tarefas" + "Configurações". Desde o I6
 * (ownership em tags/campos), Configurações é acessível a qualquer
 * profissional — a rota redireciona para Tags, aberta a todos. As sub-abas
 * gestor-only (Templates/Automações) são filtradas dentro do shell
 * (`view-task-settings.vue`), e o route guard `requireGestor` continua
 * protegendo o acesso direto por URL a essas duas.
 *
 * @param {object} [currentUser] - resolved via `this.$currentUser()` in the
 *   nav-bar. Mantido na assinatura por compatibilidade com os callers e
 *   testes; não afeta mais a composição das abas.
 */
// eslint-disable-next-line no-unused-vars
export const taskRoutes = function (currentUser) {
  return [
    {
      name: 'task.overview',
      title: 'Visão Geral',
      subtitle: 'Panorama e indicadores das tarefas do escritório',
    },
    {
      name: 'task.board',
      title: 'Tarefas',
      subtitle: 'Organize o trabalho em pastas, listas e tarefas',
    },
    {
      name: 'task.configuracoes',
      title: 'Configurações',
      subtitle: 'Tags, campos personalizados e templates',
    },
  ]
}

// Sub-navegação (BaseSegmentedTabs) da tela de Configurações — Novo Design 2026.
export const TASK_SETTINGS_SUB_NAV_OPTIONS = [
  { value: 'task.configuracoes.tags', text: 'Tags' },
  { value: 'task.configuracoes.campos', text: 'Campos' },
  { value: 'task.configuracoes.templates', text: 'Templates' },
  { value: 'task.configuracoes.modelos', text: 'Modelos de Lista' },
  { value: 'task.configuracoes.automatizacoes', text: 'Automações' },
]

// Visões Rápidas (sidebar) — listagens cross-pasta por critério temporal/pessoal.
// Ícones do padrão AAWZ (/svg): user-2 (mine), calendar-check (today),
// calendar (next7), warning (overdue), calendar-edit (noDue).
// NOTA (M10): `overdue` é status-agnóstico (inclui `done` vencido) — diverge do
// card "Atrasadas" do Overview / utils/isOverdue.js (ver task-quick-views.vue).
export const QUICK_VIEWS = [
  { key: 'mine', label: 'Minhas tarefas', icon: 'user-2' },
  { key: 'today', label: 'Hoje', icon: 'calendar-check' },
  { key: 'next7', label: 'Próximos 7 dias', icon: 'calendar' },
  { key: 'overdue', label: 'Atrasadas', icon: 'warning' },
  { key: 'noDue', label: 'Sem prazo', icon: 'calendar-edit' },
]

// ---------------------------------------------------------------------------
// spec-001 `mover-tarefas-em-massa` — copy da seleção por grupo e das ações em
// lote.
//
// A copy que depende de contagem sai como CONSTRUTOR, não como literal com
// marcador para o componente interpolar: a regra de singular/plural é lógica, e
// deixá-la no componente a duplicaria entre o rótulo acessível do controle
// (`task-group-select-control`) e a nota da faixa (`task-list-view`). Precedente
// na mesma pasta: `constants/triggerEvents.js` exporta `getTriggerEventLabel`.
// O template continua sem chamada de função — o `computed` chama o construtor.
// ---------------------------------------------------------------------------

const pluralizar = (count, singular, plural) => (count === 1 ? singular : plural)

// Nota da faixa quando o grupo está inteiramente selecionado mas o total é maior que o
// elegível: sem os dois números o usuário lê a pílula de contagem do cabeçalho
// e conclui que a seleção falhou. "Contexto" é o mesmo vocabulário do chip da
// linha (`TaskContextChip`) — precisa ser a mesma coisa aos olhos de quem lê.
export const taskGroupSelectionNote = ({ selectedCount, totalCount }) =>
  `${selectedCount} de ${totalCount} selecionadas — as marcadas como Contexto não entram.`

// A ação corretiva, isolada porque aparece em DOIS lugares: na dica do controle
// desabilitado e na nota da faixa. Fonte única — se a redação mudar, muda nos
// dois de uma vez.
const TASK_GROUP_FILTER_REMEDY = 'Ajuste ou limpe os filtros para poder selecioná-las.'

// Nota da faixa quando NENHUMA tarefa do grupo é elegível (todas são
// pais-contexto). Forma plural conforme OQ-T1 opção (a): numeral, consistente
// com a nota de descasamento acima, que já usa numerais.
//
// ★ A nota carrega a ação corretiva. A dica do controle é acionada por ponteiro,
// então depender só dela deixaria quem navega por teclado ou leitor de tela
// sabendo que não pode selecionar e sem saber o que fazer a respeito. A nota é
// nó de texto persistente numa região `role="status"` — alcança qualquer usuário.
export const taskGroupOnlyContextNote = ({ totalCount }) =>
  pluralizar(
    totalCount,
    `Nenhuma corresponde ao filtro — a única aparece apenas como Contexto. ${TASK_GROUP_FILTER_REMEDY}`,
    `Nenhuma corresponde ao filtro — as ${totalCount} aparecem apenas como Contexto. ${TASK_GROUP_FILTER_REMEDY}`
  )

// Dica do controle desabilitado (D-10). Diz O QUE FAZER, não só o que
// aconteceu: um controle inerte sem razão visível é uma porta de Norman.
export const TASK_GROUP_ONLY_CONTEXT_REASON = `Nenhuma tarefa deste grupo corresponde ao filtro. ${TASK_GROUP_FILTER_REMEDY}`

// Rótulo acessível do controle de seleção do grupo (WCAG AA). Nomeia sempre o
// grupo E a quantidade: "Selecionar todas", sem qualificar, é inútil quando há
// cinco controles iguais na mesma tela.
export const taskGroupSelectLabel = ({ state, groupLabel, eligibleCount, filterActive }) => {
  if (state === 'disabled') return `Nenhuma tarefa de ${groupLabel} corresponde ao filtro`

  const verbo = state === 'all' ? 'Desmarcar' : 'Selecionar'
  const artigo = pluralizar(eligibleCount, 'a', 'as')
  const substantivo = pluralizar(eligibleCount, 'tarefa', 'tarefas')
  const qualificador = filterActive ? ' que correspondem ao filtro' : ''

  return `${verbo} ${artigo} ${eligibleCount} ${substantivo} de ${groupLabel}${qualificador}`
}

// Movimentação parcial (D-12): a causa é falha de ESCRITA, nunca falta de
// permissão — o servidor valida permissão antes de escrever e recusa a
// requisição inteira. Culpar permissão aqui manda o usuário investigar a coisa
// errada.
// ★ Ambos os argumentos precisam contar A MESMA COISA (raízes). Passar aqui a
// contagem da subárvore contra o número de raízes produz resto negativo, e é
// DE PROPÓSITO que não há piso: um "-3" na tela faz qualquer um parar, enquanto
// um "0 restantes" numa frase que diz "parcial" e "tente novamente" passa
// batido. Absurdo visível é preferível a contradição plausível — o piso que
// existia aqui foi removido por tornar essa classe de erro menos detectável, e
// por deixar a asserção `/-\d/` do teste de regressão sem poder algum.
export const taskMovePartialMessage = ({ movedCount, requestedCount }) => {
  const restantes = requestedCount - movedCount
  const movidas = pluralizar(movedCount, 'tarefa movida', 'tarefas movidas')
  const sobrando = pluralizar(
    restantes,
    `A ${restantes} restante continua`,
    `As ${restantes} restantes continuam`
  )

  return `Movimentação parcial: ${movedCount} ${movidas} de ${requestedCount} solicitadas. ${sobrando} na lista de origem e seguem selecionadas — tente movê-las novamente.`
}

// Nenhuma raiz confirmada pelo servidor. É falha TOTAL, não movimentação
// parcial: anunciá-la como parcial em snack verde ("0 tarefas movidas de 12")
// apresenta fracasso como sucesso. O destino escolhido é preservado, então
// repetir custa um clique.
export const TASK_MOVE_NONE_MOVED =
  'Nenhuma tarefa foi movida. Elas seguem selecionadas e o destino continua escolhido — tente novamente.'

export const TASK_MOVE_NETWORK_ERROR =
  'Não foi possível mover as tarefas. Verifique sua conexão e tente novamente.'

// ATENÇÃO: estas mensagens chegam ao snackbar via `v-html`
// (`the-snackbar.vue:40`). Hoje só interpolam inteiros. NUNCA interpolar dado
// vindo do usuário aqui (título de tarefa, nome de cliente) — viraria XSS
// armazenado sem nenhum aviso de lint.
//
// Resultado das ações uma-requisição-por-tarefa (alterar e excluir). Substitui
// `Algumas tarefas falharam ao atualizar.` — verdadeira e inútil: não dizia
// quantas, então o usuário não tinha como saber o que refazer.
export const taskBatchResultMessage = ({ okCount, totalCount, verb }) => {
  const substantivo = pluralizar(okCount, 'tarefa', 'tarefas')
  if (okCount === totalCount) return `${okCount} ${substantivo} ${verb}.`

  return `${okCount} de ${totalCount} tarefas ${verb}. As demais não foram alteradas — recarregue a lista e tente novamente nas que ficaram.`
}

// Aviso do lote quando VÁRIAS tarefas concluídas vieram de solicitações do WS.
// Anexado à mensagem de resultado, nunca emitido como snack próprio: dois
// snacks no mesmo tick renderizam um só, e o segundo apagaria o resultado do
// lote — um lote parcial apareceria ao usuário como sucesso.
// Com UMA tarefa da esteira o fluxo abre o convite normal; o texto agregado
// existe porque abrir N diálogos em sequência seria pior que não avisar.
//
// AUTOSSUFICIENTE E PRIMEIRO. "N delas" logo após "as que ficaram" apontava
// para o conjunto OPOSTO — as que falharam —, quando são justamente as que
// deram certo. E num lote parcial a frase ficava no fim de ~257 caracteres,
// que não cabem nos 8s de timeout do snack: o aviso sumia antes de ser lido,
// dizendo o contrário do que é. Por isso nomeia o próprio conjunto ("das
// concluídas") e vai na FRENTE da instrução de recuperação.
export const taskBatchWsNoticeMessage = ({ count }) => {
  // Coage para número: a frase vai para `v-html` e este é o único ponto onde
  // um valor não-numérico poderia atravessar. O caller sempre passa um
  // `length`, mas "sempre" é uma propriedade do caller, não deste builder.
  count = Number(count) || 0
  // Concordância: hoje o caminho de 1 desvia para o modal, mas nada no tipo
  // impede a chamada — e "1 vieram" é o tipo de erro que sobrevive anos.
  const verbo = count === 1 ? 'veio' : 'vieram'
  const substantivo = pluralizar(count, 'solicitação', 'solicitações')
  return `Das concluídas, ${count} ${verbo} de ${substantivo} do Wealth Service — avise os solicitantes pela fila de Operação AAWZ; em lote o e-mail não sai automaticamente.`
}

// Aviso de volume ao APLICAR alterações em lote (passo único).
export const taskBatchUpdateVolumeConfirm = ({ count }) => ({
  title: `Aplicar alterações em ${count} tarefas`,
  text: 'Esta ação é aplicada tarefa por tarefa e pode levar alguns instantes. Se preferir um lote menor, cancele e reduza a seleção antes de continuar.',
  confirmLabel: 'Aplicar alterações',
})

// Aviso de volume ao EXCLUIR em lote — passo 1 de 2 (D-11). Título e rótulo são
// deliberadamente distintos dos da confirmação destrutiva que vem em seguida
// (`Remover {n} tarefas` / `Remover`): sem contraste, o segundo modal lê como o
// primeiro reaparecendo por defeito e o usuário confirma no automático.
export const taskBatchDeleteVolumeConfirm = ({ count }) => ({
  title: `Lote grande: ${count} tarefas`,
  text: 'A remoção é aplicada tarefa por tarefa e pode levar alguns instantes. Se preferir um lote menor, cancele e reduza a seleção.',
  confirmLabel: 'Continuar',
})

// Erro ao excluir tarefas em lote — fonte única para os DOIS branches de falha
// de `deleteAll` (task-batch-edit-row.vue): a resposta com `status !== 200` e o
// `catch` de erro de rede. Antes cada branch tinha sua própria string
// ('Erro ao remover tarefas.'), idêntica e sem ação corretiva — o usuário ficava
// sem saber o que fazer a seguir.
export const TASK_BATCH_DELETE_ERROR = 'Erro ao remover tarefas. Tente novamente.'

// Delete de UMA tarefa: o caminho de rede e o do lote (`DELETE /tasks/batch`),
// que responde 200 com `count: 0` + `skipped` quando a tarefa nao pode ser
// removida. `apiTarefas.deleteTask` traduz esse `skipped` de volta para 404/403
// com as mensagens abaixo — sem isso, falta de permissao apareceria como snack
// VERDE de sucesso, porque os chamadores individuais decidem por `status === 200`.
export const TASK_DELETE_NOT_FOUND =
  'Tarefa não encontrada. Ela pode ter sido removida por outra pessoa — atualize a lista.'
export const TASK_DELETE_FORBIDDEN =
  'Você não tem permissão para remover esta tarefa. Peça a um gestor do escritório.'

// Anunciado ANTES de mover: é o que impede a contagem devolvida pelo servidor
// (maior que a seleção, porque inclui descendentes) de parecer defeito.
export const TASK_MOVE_SUBTASK_HINT = 'As subtarefas acompanham a tarefa principal.'
